import React, { type FC } from "react";

import cn from "classnames";
import getEmojiRegex from "emoji-regex";

import { type TBadgeSizes } from "../../assets/types/icons";
import { extractProps } from "../../utils/react";
import { Icon } from "../Icon";

import type { BadgeSize } from "./types";
import type { PropsWithTsId } from "../../types/react";

import classes from "./Badge.styl";

interface IBadgeBaseProps {
    size?: BadgeSize;
}

interface StrokeBadgeProps {
    stroke?: boolean;
}

type SuitableIconSizes = Extract<TBadgeSizes, IBadgeBaseProps>;

export type IconBadgeProps = Omit<SuitableIconSizes, "size"> &
    IBadgeBaseProps &
    StrokeBadgeProps & {
        content: "icon";
    };

export interface IEmojiBadgeProps extends IBadgeBaseProps, StrokeBadgeProps {
    content: "emoji";
    /** Эмоджи */
    emoji: string;
}

export interface IImageBadgeProps extends IBadgeBaseProps, StrokeBadgeProps {
    content: "image";
    /** URL изображения для бейджа с соответствующим типом */
    src: string;
    /** Alt-атрибут изображения для бейджа с соответствующим изображения */
    alt: string;
    /** Класс для цвета заднего фона значка */
    colorsStyles?: string;
}

export type BadgeProps = (
    | IconBadgeProps
    | IEmojiBadgeProps
    | IImageBadgeProps
) &
    Partial<PropsWithTsId>;

export const IMAGE_BADGE_WRAPPER_TEST_ID = "image-badge-wrapper-test-id";
const BADGE_DISPLAY_NAME = "Badge";

const isImageBadge = (props: BadgeProps): props is IImageBadgeProps => {
    return props.content === "image";
};

const isEmojiBadge = (props: BadgeProps): props is IEmojiBadgeProps => {
    return props.content === "emoji";
};

/**
 * Базовый компонент, который сообщает пользователю о статусе или другом опознавательном признаке для другого элемента интерфейса.
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=2-13958&t=xOS5YJTI37Iwp4AM-1" target="_blank">Спека</a>
 * @constructor
 */
export const Badge: FC<BadgeProps> = (props) => {
    const { size = "16", content, stroke, "data-tsid": tsid } = props;
    const { colorsStyles, icon } = extractProps(props, [
        "colorsStyles",
        "icon",
    ]);

    const badgeClasses = cn(
        classes["badge"],
        classes[`__size-${size}`],
        classes[`__content-${content}` as keyof typeof classes],
        {
            [classes["__stroke"]]: stroke,
        },
        isImageBadge(props) && colorsStyles,
    );

    const dataTestId = tsid || IMAGE_BADGE_WRAPPER_TEST_ID;

    if (isImageBadge(props)) {
        const { src, alt } = props;

        return (
            <div
                className={badgeClasses}
                data-tsid={dataTestId}
                data-uikit={BADGE_DISPLAY_NAME}
            >
                <img className={classes["image"]} src={src} alt={alt} />
            </div>
        );
    }

    if (isEmojiBadge(props)) {
        const { emoji } = props;

        if (!getEmojiRegex().test(emoji)) {
            throw new Error(
                `Последовательность '${emoji}' не является эмоджи!`,
            );
        }
        return (
            <span
                className={badgeClasses}
                data-tsid={dataTestId}
                data-uikit={BADGE_DISPLAY_NAME}
            >
                {emoji}
            </span>
        );
    }

    const iconProps = {
        size,
        icon,
    } as SuitableIconSizes;

    return (
        <span
            className={badgeClasses}
            data-tsid={dataTestId}
            data-uikit={BADGE_DISPLAY_NAME}
        >
            <Icon {...iconProps} />
        </span>
    );
};

Badge.displayName = BADGE_DISPLAY_NAME;

export default Badge;
